/**
 * ProfitLossCard - monthly P&L from the accounting feature.
 * Headline net for the latest month, then a diverging bar per month (profit up
 * in green, loss down in red) off a zero baseline.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../../platform/auth/AuthContext';
import { ProfitLossService, type ProfitLossMonth } from '../../services/ProfitLossService';
import { SalesSummaryService } from '../../services/SalesSummaryService';
import { ProfitLossSkeleton } from './DashboardLoading';
import { fmtMoney } from './utils';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ProfitLossChart } from '../../shared-ui/home/ProfitLossChart';
import { summariseProfitLoss } from '../../shared-ui/home/homeModels';

type LoadState = 'loading' | 'ready' | 'error';

export function ProfitLossCard() {
  const { activeWorkspaceId } = useAuth();
  // Seed from the last-known series so the card paints instantly, like the
  // cache-backed cards beside it, instead of flashing "Loading...". One read
  // covers both the data and the initial load state (months + state move
  // together, so they can never disagree).
  const [{ months, state }, setData] = useState<{ months: ProfitLossMonth[] | null; state: LoadState }>(() => {
    const cached = activeWorkspaceId ? ProfitLossService.getCachedMonthly(activeWorkspaceId, 6) : null;
    return { months: cached, state: cached ? 'ready' : 'loading' };
  });

  useEffect(() => {
    let cancelled = false;
    if (!activeWorkspaceId) {
      setData({ months: [], state: 'ready' });
      return;
    }
    // Stale-while-revalidate: show the cached series immediately (no loading
    // flash), only fall back to "Loading..." when there is nothing cached.
    const cached = ProfitLossService.getCachedMonthly(activeWorkspaceId, 6);
    setData((prev) => (cached ? { months: cached, state: 'ready' } : { ...prev, state: 'loading' }));
    const load = () => ProfitLossService.getMonthly(activeWorkspaceId, 6)
      .then((data) => {
        if (!cancelled) setData(prev => prev.state === 'ready' && JSON.stringify(prev.months) === JSON.stringify(data) ? prev : { months: data, state: 'ready' });
      })
      .catch(() => {
        // Keep the cached series on a refresh failure; only surface the error
        // state when we have nothing to show.
        if (!cancelled && !cached) setData((prev) => ({ ...prev, state: 'error' }));
      });
    void load();
    const unsubscribe = SalesSummaryService.subscribe(activeWorkspaceId, () => { void load(); });
    return () => {
      unsubscribe();
      cancelled = true;
    };
  }, [activeWorkspaceId]);

  const isPhone = useMediaQuery('(max-width: 767px)');

  if (state === 'loading') {
    return <ProfitLossSkeleton />;
  }
  if (state === 'error') {
    return (
      <div className="px-5 py-8 text-center text-[12px] text-muted-foreground font-apple">
        Couldn&apos;t load profit &amp; loss.
      </div>
    );
  }
  if (!months || months.length === 0) {
    return (
      <div className="px-5 py-8 text-center text-[12px] text-muted-foreground font-apple">
        No accounting activity yet.
      </div>
    );
  }

  // Phones draw the shared chart; the summary maths is shared either way.
  if (isPhone) return <ProfitLossChart months={months} />;
  const { hasActivity, zeroTopPercent: zeroTopPct, periodNet, periodIncome, periodExpenses, bars } = summariseProfitLoss(months);
  const positiveNet = periodNet >= 0;

  return (
    <div className="flex h-full min-h-0 flex-col px-5 pb-3 pt-1 font-apple @[700px]/dash:pb-4">
      <div className="mb-2.5 shrink-0 @[700px]/dash:mb-3">
        <p
          className="text-[18px] font-semibold tabular-nums leading-none"
          style={{
            color: hasActivity
              ? positiveNet ? 'var(--status-success)' : 'var(--status-danger)'
              : 'var(--muted-foreground)',
          }}
        >
          {positiveNet ? '' : '-'}{fmtMoney(Math.abs(periodNet))}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground tabular-nums @[700px]/dash:mt-1.5 @[700px]/dash:text-[12px]">
          {!hasActivity
            ? 'No activity in the last 6 months'
            : <>6-month net · {fmtMoney(periodIncome)} in, {fmtMoney(periodExpenses)} out</>}
        </p>
      </div>

      {/* flex-1 lets the chart absorb the card height, which the grid row now
          bounds to the taller sibling card rather than the viewport. */}
      <div className="relative min-h-[82px] flex-1 @[700px]/dash:min-h-[130px]">
        {/* zero baseline */}
        <div className="absolute inset-x-0 h-px bg-border/70" style={{ top: `${zeroTopPct}%` }} />
        <div className="absolute inset-0 flex justify-around gap-3">
          {bars.map((bar) => {
            const color = bar.up ? 'var(--status-success)' : 'var(--status-danger)';
            return (
              <div key={bar.monthKey} className="relative flex-1">
                <div
                  className="absolute left-1/2 -translate-x-1/2 w-full max-w-[24px] rounded-[5px] shadow-[0_6px_12px_-7px_rgba(0,0,0,0.35)] transition-[height] duration-500 ease-out"
                  style={{
                    height: `${bar.heightPercent}%`,
                    top: `${bar.topPercent}%`,
                    background: `linear-gradient(180deg, color-mix(in srgb, ${color} 86%, white 14%), ${color})`,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-1.5 flex shrink-0 justify-around gap-3">
        {months.map((m) => (
          <span key={m.monthKey} className="flex-1 text-center text-[10px] text-muted-foreground">
            {m.label}
          </span>
        ))}
      </div>
    </div>
  );
}
